(() => {
  'use strict';

  const NAMESPACE = 'camoVariantUrlContext';
  if (window[NAMESPACE]) return;

  const PARAMS = {
    type: {
      query: 'filter_type',
      selector: '.xo-product-info-variant--type',
      values: new Map([
        ['wraps', 'Wraps'],
        ['cones', 'Cones']
      ])
    },
    packType: {
      query: 'filter_pack_type',
      selector: '.xo-product-info-variant--pack-type',
      values: new Map([
        ['boxes', 'Boxes'],
        ['pouches', 'Pouches']
      ])
    }
  };

  const ACTIVE_CLASS = 'xo-product-info-variant--camo-url-context-active';
  const HIDDEN_CLASS = 'xo-field-radio--camo-url-context-hidden';
  const COMPACT_GROUP_CLASS = 'xo-product-info-variant--camo-url-context-static';
  const HEADER_CLASS = 'xo-product-info-content__header--camo-url-context';
  const SUMMARY_CLASS = 'camo-variant-url-context-summary';
  let context = readContext(window.location.href);
  let observer = null;
  let applyQueued = false;

  function normalize(value) {
    return String(value || '').trim().toLocaleLowerCase('en-US');
  }

  function readContext(urlValue) {
    let url;
    try {
      url = new URL(urlValue, window.location.href);
    } catch (_error) {
      return {};
    }

    const next = {};
    Object.entries(PARAMS).forEach(([key, config]) => {
      const normalized = normalize(url.searchParams.get(config.query));
      if (config.values.has(normalized)) next[key] = config.values.get(normalized);
    });
    return next;
  }

  function findValueLabel(group, allowedValue) {
    return Array.from(group.querySelectorAll('label.xo-field-radio')).find((label) => {
      const input = label.querySelector('input[type="radio"]');
      return input && normalize(input.value) === normalize(allowedValue);
    }) || null;
  }

  function readableText(element) {
    if (!element) return '';
    return String(element.innerText || element.textContent || '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function readCardContent(label) {
    return {
      title: readableText(label.querySelector('.xo-field-radio__card-title')),
      subtitle: readableText(label.querySelector('.xo-field-radio__card-subtitle'))
    };
  }

  function resetPresentation() {
    document.querySelectorAll(`.${SUMMARY_CLASS}`).forEach((summary) => summary.remove());
    document.querySelectorAll(`.${HEADER_CLASS}`).forEach((header) => header.classList.remove(HEADER_CLASS));
    document.querySelectorAll(`.${COMPACT_GROUP_CLASS}`).forEach((group) => {
      group.classList.remove(COMPACT_GROUP_CLASS);
      group.removeAttribute('aria-hidden');
    });
  }

  function resetGroup(group) {
    group.classList.remove(ACTIVE_CLASS);
    group.querySelectorAll(`.${HIDDEN_CLASS}`).forEach((label) => {
      label.classList.remove(HIDDEN_CLASS);
      label.removeAttribute('aria-hidden');
    });
  }

  function applyGroup(config, allowedValue) {
    const summaries = [];

    document.querySelectorAll(config.selector).forEach((group) => {
      resetGroup(group);
      if (!allowedValue) return;

      const allowedLabel = findValueLabel(group, allowedValue);
      if (!allowedLabel) return;

      const labels = Array.from(group.querySelectorAll('label.xo-field-radio'));
      const labelsToHide = labels.filter((label) => label !== allowedLabel);

      // A naturally single-value option is not compacted. This presentation is
      // reserved for groups explicitly reduced by a valid URL context.
      if (!labelsToHide.length) return;

      const cardContent = readCardContent(allowedLabel);
      if (!cardContent.title) return;

      labelsToHide.forEach((label) => {
        label.classList.add(HIDDEN_CLASS);
        label.setAttribute('aria-hidden', 'true');
      });

      group.classList.add(ACTIVE_CLASS, COMPACT_GROUP_CLASS);
      group.setAttribute('aria-hidden', 'true');
      summaries.push({ group, ...cardContent });
    });

    return summaries;
  }

  function addSummaryItem(summary, item) {
    const separatorBefore = document.createElement('span');
    separatorBefore.className = 'camo-variant-url-context-summary__separator';
    separatorBefore.setAttribute('aria-hidden', 'true');
    separatorBefore.textContent = '|';

    const value = document.createElement('span');
    value.className = 'camo-variant-url-context-summary__value';
    value.textContent = item.title;

    summary.append(separatorBefore, value);

    if (item.subtitle) {
      const separatorAfter = separatorBefore.cloneNode(true);
      const subtitle = document.createElement('span');
      subtitle.className = 'camo-variant-url-context-summary__subtitle';
      subtitle.textContent = item.subtitle;
      summary.append(separatorAfter, subtitle);
    }
  }

  function renderSummaries(items) {
    const itemsByContent = new Map();

    items.forEach((item) => {
      const content = item.group.closest('.xo-product-info-content');
      if (!content) return;
      if (!itemsByContent.has(content)) itemsByContent.set(content, []);
      itemsByContent.get(content).push(item);
    });

    itemsByContent.forEach((contentItems, content) => {
      const header = content.querySelector('.xo-product-info-content__header');
      const title = header?.querySelector('.xo-product-info-content__title');
      if (!header || !title) return;

      const summary = document.createElement('span');
      summary.className = SUMMARY_CLASS;
      summary.style.setProperty(
        '--camo-variant-url-context-title-size',
        window.getComputedStyle(title).fontSize
      );

      contentItems
        .sort((a, b) => {
          const position = a.group.compareDocumentPosition(b.group);
          return position & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
        })
        .forEach((item) => addSummaryItem(summary, item));

      title.insertAdjacentElement('afterend', summary);
      header.classList.add(HEADER_CLASS);
    });
  }

  function apply() {
    resetPresentation();
    const summaries = [
      ...applyGroup(PARAMS.type, context.type),
      ...applyGroup(PARAMS.packType, context.packType)
    ];
    renderSummaries(summaries);
  }

  function queueApply() {
    if (applyQueued) return;
    applyQueued = true;
    window.requestAnimationFrame(() => {
      applyQueued = false;
      apply();
    });
  }

  function preserveContextInUrl(urlValue) {
    if (!urlValue || (!context.type && !context.packType)) return urlValue;

    let url;
    try {
      url = new URL(urlValue, window.location.href);
    } catch (_error) {
      return urlValue;
    }

    if (url.origin !== window.location.origin || !url.pathname.startsWith('/products/')) return urlValue;

    if (context.type) url.searchParams.set(PARAMS.type.query, context.type);
    if (context.packType) url.searchParams.set(PARAMS.packType.query, context.packType);

    if (typeof urlValue === 'string' && !/^[a-zA-Z][a-zA-Z\d+.-]*:/.test(urlValue)) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
    return url.toString();
  }

  function patchHistory(methodName) {
    const original = history[methodName];
    if (typeof original !== 'function') return;

    history[methodName] = function patchedHistory(state, title, url) {
      const result = original.call(this, state, title, preserveContextInUrl(url));
      queueApply();
      return result;
    };
  }

  function handlePopState() {
    context = readContext(window.location.href);
    queueApply();
  }

  function handleVariantChange(event) {
    if (event.target.matches('.xo-product-info-variant input[type="radio"]')) queueApply();
  }

  function startObserver() {
    if (!document.body || observer) return;
    observer = new MutationObserver((mutations) => {
      const relevant = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some((node) =>
          node.nodeType === Node.ELEMENT_NODE &&
          (node.matches?.('.xo-product-info-variant, xo-product') || node.querySelector?.('.xo-product-info-variant'))
        )
      );
      if (relevant) queueApply();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  window[NAMESPACE] = { apply };
  patchHistory('pushState');
  patchHistory('replaceState');
  window.addEventListener('popstate', handlePopState);
  window.addEventListener('pageshow', queueApply);
  document.addEventListener('change', handleVariantChange);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      apply();
      startObserver();
    }, { once: true });
  } else {
    apply();
    startObserver();
  }
})();
